import {
    FacebookLoginProvider,
    GoogleInitOptions,
    GoogleLoginProvider,
    MicrosoftLoginProvider,
    SocialAuthServiceConfig,
} from '@abacritt/angularx-social-login';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { hideProgress } from 'app/main/shared/http-utils';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, of, Subscription, timer } from 'rxjs';
import { catchError, delayWhen, first, retryWhen, shareReplay, tap } from 'rxjs/operators';
import { ServerInfo } from '../model/server-info.model';
import { SystemParameter } from '../model/system-parameter.model';
import { TimeZone } from '../model/timezone.model';
import { RestResult } from '../shared/rest-result.model';
import { ImageStoreService } from '../state/image/image.store';
import { EventManager } from './event-manager.service';

const googleLoginOptions: GoogleInitOptions = {
    scopes: 'profile email',
    prompt: 'none',
    oneTapEnabled: false,
};

@Injectable({
    providedIn: 'root',
})
export class GatewayService implements OnDestroy {
    socialLoginConfig = new BehaviorSubject<SocialAuthServiceConfig>({ providers: [] });
    subscriberStudentDataChanged: Subscription;
    constructor(
        private http: HttpClient,
        private eventManager: EventManager,
        private storeService: ImageStoreService
    ) {
        this.socialLoginConfigInit();
        this.init();
    }

    private init() {
        this.subscriberStudentDataChanged = this.eventManager.subscribe('student-data-changed', content => {
            const studentId = content.message.message;
            if (studentId) {
                if (this.storeService.get(studentId)) {
                    this.storeService.delete(studentId);
                    this.eventManager.broadcast({
                        name: 'refresh-student-data',
                        studentId: studentId,
                    });
                }
            }
        });
    }

    getTimeZones(): Observable<TimeZone[]> {
        return this.http.get<TimeZone[]>('/api/timezones');
    }

    socialLoginConfigInit() {
        const res: SocialAuthServiceConfig = {
            autoLogin: false,
            providers: [
                {
                    id: GoogleLoginProvider.PROVIDER_ID,
                    provider: new GoogleLoginProvider(environment.serverInfo?.googleClientId, googleLoginOptions),
                },
                {
                    id: FacebookLoginProvider.PROVIDER_ID,
                    provider: new FacebookLoginProvider(environment.serverInfo?.facebookClientId),
                },
                {
                    id: MicrosoftLoginProvider.PROVIDER_ID,
                    provider: new MicrosoftLoginProvider(environment.serverInfo?.microsoftClientId),
                },
            ],
            onError: (err: any) => {
                console.warn(err);
            },
        };
        console.log('SOCIAL_LOGIN', res);
        this.socialLoginConfig.next(res);
    }

    serverInfo(): Observable<ServerInfo> {
        console.log('GATEWAY', 'api-public/serverInfo');
        return this.http
            .get<any>('/api-public/serverInfo', {
                headers: hideProgress(),
            })
            .pipe(
                shareReplay(),
                retryWhen(errors => {
                    return errors.pipe(
                        delayWhen(() => timer(5000)),
                        tap(() => console.log('retrying...server'))
                    );
                }),
                catchError(() => {
                    return of(undefined).pipe(first());
                })
            );
    }

    testCron(cron: string): Observable<HttpResponse<boolean>> {
        return this.http.post<boolean>('/api-public/test-cron', { value: cron }, { observe: 'response' });
    }

    systemParameters(): Observable<RestResult<SystemParameter>> {
        return this.http.get<any>(
            `/api-public/system-parameters?id=${new Date().getTime()}${Math.floor(Math.random() * 1000000)}`
        );
    }

    getServices(): Observable<any> {
        return this.http.get<any>('/api-public/ui-urls');
    }

    ngOnDestroy(): void {
        if (this.subscriberStudentDataChanged) {
            this.subscriberStudentDataChanged.unsubscribe();
        }
    }
}
